/*
 * Bundle box builder.
 *
 * Lets the shopper pick N variants out of a collection and adds them to the
 * cart in one request: a "box" line item followed by the chosen variants, all
 * tied together by a shared `_box_id` line item property.
 */

class BundleBox extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.boxVariantId = this.dataset.boxVariantId;
    this.boxTitle = this.dataset.boxProductTitle || '';
    this.min = parseInt(this.dataset.boxMin, 10) || 1;
    this.max = parseInt(this.dataset.boxMax, 10) || this.min;
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    // Box discount (%) and the box product's own price, both in play for the total.
    this.discountPercent = parseFloat(this.dataset.boxDiscount) || 0;
    this.boxPrice = parseInt(this.dataset.boxPrice, 10) || 0;
    // Matched by the discount app's Line Item Attribute rule.
    this.boxTier = this.dataset.boxTier || '';
    this.useDrawer = this.dataset.cartDrawer === 'true';

    this.text = {
      add: this.dataset.textAdd || 'Adaugă',
      chooseSize: this.dataset.textChooseSize || 'Alege mărimea',
      soldOut: this.dataset.textSoldOut || 'Stoc epuizat',
      progress: this.dataset.textProgress || '[count] / [max]',
      remaining: this.dataset.textRemaining || 'Mai adaugă [remaining]',
      ready: this.dataset.textReady || '',
      full: this.dataset.textFull || '',
      cta: this.dataset.textCta || 'Adaugă în coș',
      empty: this.dataset.textEmpty || '',
      update: this.dataset.textUpdate || 'Actualizează cutia',
      boxProperty: this.dataset.textBoxProperty || 'Din cutie',
      boxCode: this.dataset.textBoxCode || 'Cod cutie',
      boxContents: this.dataset.textBoxContents || 'Conținut cutie',
      savings: this.dataset.textSavings || 'economisești [amount]',
    };

    /** @type {Map<number, object>} variantId -> selection */
    this.selection = new Map();

    this.progressBar = this.querySelector('[data-bundle-progress-bar]');
    this.progressText = this.querySelector('[data-bundle-progress-text]');
    this.bar = this.querySelector('[data-bundle-bar]');
    this.barCount = this.querySelector('[data-bundle-bar-count]');
    this.chips = this.querySelector('[data-bundle-chips]');
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.totalCompareEl = this.querySelector('[data-bundle-total-compare]');
    this.savingsEl = this.querySelector('[data-bundle-savings]');
    this.submitButton = this.querySelector('[data-bundle-submit]');
    this.ctaLabel = this.querySelector('[data-bundle-cta-label]');
    this.errorEl = this.querySelector('[data-bundle-error]');

    this.cards = Array.from(this.querySelectorAll('[data-bundle-card]')).map((el) => new BundleCard(el, this));

    this.submitButton.addEventListener('click', () => this.submit());
    this.chips.addEventListener('click', (event) => {
      const button = event.target.closest('[data-chip-remove]');
      if (!button) return;
      this.setQuantity(parseInt(button.dataset.chipRemove, 10), 0);
    });

    this.editBoxId = null;
    this.editBoxKeys = [];

    // A box re-opened from the cart is already safe in the cart, so only a
    // new box is remembered as a draft (see assets/box-draft.js).
    this.draftEnabled = !new URLSearchParams(window.location.search).has('box') && Boolean(window.BoxDraft);

    if (this.draftEnabled) {
      this.restoreDraft();
    } else {
      this.restoreFromCart();
    }
    this.render();
  }

  /* ---------------------------------------------------------------- state */

  get count() {
    let total = 0;
    this.selection.forEach((item) => {
      total += item.quantity;
    });
    return total;
  }

  /** Sum of the chosen items at full price. */
  get total() {
    let total = 0;
    this.selection.forEach((item) => {
      total += item.price * item.quantity;
    });
    return total;
  }

  /**
   * What the box discount takes off one unit.
   *
   * The discount app allocates per line and truncates the fraction of a bani,
   * so a 8% discount on 11,21 lei is 0,89 and not 0,90. Rounding the sum of
   * the box instead, or rounding each unit to nearest, drifts a bani or two
   * away from what the cart and the checkout actually charge. Liquid's integer
   * `divided_by` in card-product-bundle.liquid truncates the same way, so the
   * card prices rendered server-side already agree with this.
   */
  unitDiscount(cents) {
    if (!this.discountPercent) return 0;
    return Math.floor((cents * this.discountPercent) / 100);
  }

  /** What the box discount takes off the contents, allocated the same way. */
  get discountAmount() {
    if (!this.discountPercent) return 0;
    let saved = 0;
    this.selection.forEach((item) => {
      saved += this.unitDiscount(item.price) * item.quantity;
    });
    return saved;
  }

  /** What the shopper actually pays: contents, less the discount, plus the box. */
  get grandTotal() {
    return this.total - this.discountAmount + (this.count > 0 ? this.boxPrice : 0);
  }

  /** A single item's price with the box discount applied. */
  discountedPrice(cents) {
    return cents - this.unitDiscount(cents);
  }

  get isFull() {
    return this.count >= this.max;
  }

  /** How many more items the box can still take. */
  get room() {
    return Math.max(this.max - this.count, 0);
  }

  quantityFor(variantId) {
    const item = this.selection.get(variantId);
    return item ? item.quantity : 0;
  }

  quantityForProduct(productId) {
    let total = 0;
    this.selection.forEach((item) => {
      if (item.productId === productId) total += item.quantity;
    });
    return total;
  }

  /**
   * Sets the absolute quantity of a variant in the box. `payload` is only
   * needed the first time a variant is added.
   */
  setQuantity(variantId, quantity, payload) {
    const existing = this.selection.get(variantId);

    if (quantity <= 0) {
      this.selection.delete(variantId);
    } else {
      const room = this.max - this.count + (existing ? existing.quantity : 0);
      const capped = Math.min(quantity, room);
      if (capped <= 0) return;
      if (existing) {
        existing.quantity = capped;
      } else if (payload) {
        this.selection.set(variantId, Object.assign({}, payload, { quantity: capped }));
      }
    }

    this.saveDraft();
    this.render();
  }

  /* ---------------------------------------------------------------- draft */

  /** Remembers the picks so far, so leaving the page doesn't lose the box. */
  saveDraft() {
    if (!this.draftEnabled || this.restoring) return;

    const items = [];
    this.selection.forEach((item) => {
      items.push({ variantId: item.variantId, quantity: item.quantity, title: item.title, image: item.image });
    });

    window.BoxDraft.save(this.boxVariantId, {
      url: window.location.pathname,
      title: this.boxTitle,
      min: this.min,
      max: this.max,
      items,
    });
  }

  clearDraft() {
    if (this.draftEnabled) window.BoxDraft.clear(this.boxVariantId);
  }

  /** Puts back an unfinished box, minus anything that sold out meanwhile. */
  restoreDraft() {
    const draft = window.BoxDraft.get(this.boxVariantId);
    if (!draft) return;

    this.restoring = true;
    draft.items.forEach((item) => {
      const variantId = parseInt(item.variantId, 10);
      const card = this.cards.find((entry) =>
        entry.variants.some((variant) => variant.id === variantId && variant.available)
      );
      if (card) card.selectVariantId(variantId, parseInt(item.quantity, 10) || 1);
    });
    this.restoring = false;

    // Re-save what actually made it back in, so the reminder bar agrees.
    this.saveDraft();
  }

  /* --------------------------------------------------------------- render */

  render() {
    const count = this.count;
    const remaining = this.max - count;
    const missing = Math.max(this.min - count, 0);

    const fill = this.max > 0 ? Math.min((count / this.max) * 100, 100) : 0;
    if (this.progressBar) this.progressBar.style.width = `${fill}%`;

    let message;
    if (count >= this.max) {
      message = this.text.full;
    } else if (count >= this.min) {
      message = this.text.ready;
    } else {
      message = this.text.remaining;
    }
    const fill_in = (template) =>
      (template || '')
        .replace('[count]', count)
        .replace('[min]', this.min)
        .replace('[max]', this.max)
        .replace('[remaining]', count >= this.min ? remaining : missing);

    if (this.progressText) this.progressText.textContent = fill_in(message);
    if (this.barCount) this.barCount.textContent = fill_in(this.text.progress);

    this.renderChips();

    if (this.totalEl) this.totalEl.textContent = this.formatMoneyExact(this.grandTotal);

    const saving = this.discountAmount;

    if (this.totalCompareEl) {
      this.totalCompareEl.hidden = saving <= 0;
      if (saving > 0) {
        this.totalCompareEl.textContent = this.formatMoneyExact(this.total + this.boxPrice);
      }
    }

    if (this.savingsEl) {
      this.savingsEl.hidden = saving <= 0;
      if (saving > 0) {
        this.savingsEl.textContent = this.text.savings.replace('[amount]', this.formatMoneyExact(saving));
      }
    }

    const ready = count >= this.min && count <= this.max;
    this.submitButton.disabled = !ready;
    this.submitButton.classList.toggle('is-ready', ready);
    if (this.ctaLabel) this.ctaLabel.textContent = this.text.cta;
    this.bar.classList.toggle('is-active', count > 0);

    this.cards.forEach((card) => card.render());
  }

  renderChips() {
    if (!this.chips) return;

    if (this.selection.size === 0) {
      this.chips.innerHTML = `<li class="bundle-box__chip bundle-box__chip--empty">${this.text.empty}</li>`;
      return;
    }

    const markup = [];
    this.selection.forEach((item) => {
      const details = [item.color, item.size].filter(Boolean).join(' · ');
      markup.push(`
        <li class="bundle-box__chip">
          ${item.image ? `<img src="${item.image}" alt="" width="32" height="40" loading="lazy">` : ''}
          <span class="bundle-box__chip-text">
            <span class="bundle-box__chip-title">${escapeHtml(item.title)}</span>
            ${details ? `<span class="bundle-box__chip-meta">${escapeHtml(details)}</span>` : ''}
          </span>
          ${item.quantity > 1 ? `<span class="bundle-box__chip-qty">×${item.quantity}</span>` : ''}
          <button type="button" class="bundle-box__chip-remove" data-chip-remove="${item.variantId}" aria-label="Elimină ${escapeHtml(item.title)}">&times;</button>
        </li>
      `);
    });
    this.chips.innerHTML = markup.join('');
  }

  /** Card prices, to match Liquid's `money_without_trailing_zeros`. */
  formatMoney(cents) {
    return formatBundleMoney(cents, this.moneyFormat).replace(/([.,])00(?!\d)/, '');
  }

  /**
   * Bar totals, to match the cart, which renders the box total with `money`.
   * Trailing zeros stay: 27,00 lei here and 27,00 lei there.
   */
  formatMoneyExact(cents) {
    return formatBundleMoney(cents, this.moneyFormat);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  /* ----------------------------------------------------------- cart write */

  async submit() {
    if (this.submitButton.disabled) return;

    this.showError('');
    this.submitButton.classList.add('loading');
    this.submitButton.disabled = true;

    const boxId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    // Short human-readable reference so a picker can tell two boxes apart on
    // the same order without reading the internal id.
    const boxCode = `#${boxId.split('-').pop().toUpperCase()}`;
    const contents = [];
    const items = [];

    this.selection.forEach((item) => {
      const details = [item.color, item.size].filter(Boolean).join(' / ');
      contents.push(`${item.quantity}× ${item.title}${details ? ` (${details})` : ''}`);
      // The underscored keys are plumbing. Underscored properties never reach
      // the packing slip, so the marker the warehouse reads is a plain one.
      const properties = {
        _box_id: boxId,
        _box_child: '1',
      };
      if (this.boxTier) properties._box_tier = this.boxTier;
      properties[this.text.boxProperty] = `${this.boxTitle} ${boxCode}`;

      items.push({
        id: item.variantId,
        quantity: item.quantity,
        properties,
      });
    });

    const boxProperties = {
      _box_id: boxId,
      _box_size: String(this.count),
    };
    boxProperties[this.text.boxCode] = boxCode;
    boxProperties[this.text.boxContents] = contents.join(', ');

    items.unshift({
      id: parseInt(this.boxVariantId, 10),
      quantity: 1,
      properties: boxProperties,
    });

    const drawer = document.querySelector('cart-drawer');
    const editing = Boolean(this.editBoxId);
    const sectionsPayload = {};
    if (drawer && this.useDrawer) {
      sectionsPayload.sections = drawer.getSectionsToRender().map((section) => section.id);
      sectionsPayload.sections_url = window.location.pathname;
    }

    // Shopify's batch /cart/add.js unshifts each item, so the cart (and with it
    // the checkout, the order and the packing slip) ends up in the reverse of
    // the order sent. Send it reversed so the box header lands first, followed
    // by its contents in the order the shopper picked them.
    items.reverse();

    try {
      // Add the new box first, so a failure never loses the box being edited.
      const addBody = Object.assign({ items }, editing ? {} : sectionsPayload);
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(addBody),
      });
      let data = await response.json();

      if (data.status) {
        this.showError(data.description || data.message);
        return;
      }

      // Only once the replacement is safely in the cart, drop the old lines.
      //
      // The keys captured when the box was opened for editing are NOT safe to
      // reuse here. An automatic discount on the box contents is reallocated
      // the moment the replacement lines land, and Shopify rewrites the `key`
      // of every line whose discount allocation changed. /cart/update.js then
      // fails to match the stale key, falls back to matching by variant id and
      // zeroes the line we have just added instead of the one we meant to drop
      // - which left the new box header attached to the old box's contents.
      if (editing) {
        data = await this.removeBoxLines(this.editBoxId, sectionsPayload);
        this.editBoxId = null;
        this.editBoxKeys = [];
        this.text.cta = this.dataset.textCta || this.text.cta;
      }

      this.selection.clear();
      this.clearDraft();
      this.render();

      if (drawer && this.useDrawer && data.sections) {
        drawer.renderContents(data);
      } else {
        window.location = window.routes.cart_url;
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-box', cartData: data });
      }
    } catch (error) {
      console.error(error);
      this.showError('A apărut o eroare. Te rugăm să încerci din nou.');
    } finally {
      this.submitButton.classList.remove('loading');
      this.render();
    }
  }

  /**
   * Removes every cart line belonging to `boxId`, resolving the line keys
   * against the cart as it is right now rather than against keys captured
   * earlier - discount reallocation rewrites them (see `submit`).
   */
  async removeBoxLines(boxId, sectionsPayload) {
    const readCart = () =>
      fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(
        (response) => response.json()
      );
    const linesFor = (cart) =>
      (cart.items || []).filter((item) => item.properties && item.properties._box_id === boxId);

    let cart = await readCart();
    let data = null;
    const keys = linesFor(cart).map((line) => line.key);

    // Fast path: one batch with keys that were read a moment ago.
    if (keys.length) {
      const updates = {};
      keys.forEach((key) => {
        updates[key] = 0;
      });
      data = await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ updates }, sectionsPayload)),
      }).then((response) => response.json());
    }

    // Dropping one line can reallocate the discount across the rest and so
    // rewrite their keys mid-batch. Mop up whatever survived, one line at a
    // time, re-resolving the key before each call.
    for (let guard = 0; guard < 20; guard += 1) {
      cart = data && data.items ? data : await readCart();
      const line = linesFor(cart)[0];
      if (!line) break;
      data = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ id: line.key, quantity: 0 }, sectionsPayload)),
      }).then((response) => response.json());
    }

    return data || this.fetchCartState(sectionsPayload);
  }

  /** Current cart plus freshly rendered sections, without changing anything. */
  async fetchCartState(sectionsPayload) {
    const response = await fetch(`${window.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ updates: {} }, sectionsPayload)),
    });
    return response.json();
  }

  /**
   * `?box=<id>` on the URL re-opens an existing box for editing: its lines are
   * loaded back into the builder and replaced when the shopper saves.
   */
  async restoreFromCart() {
    const boxId = new URLSearchParams(window.location.search).get('box');
    if (!boxId) return;

    try {
      const cart = await fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
      const lines = cart.items.filter((line) => line.properties && line.properties._box_id === boxId);
      if (!lines.length) return;

      this.editBoxId = boxId;
      this.editBoxKeys = lines.map((line) => line.key);

      lines
        .filter((line) => line.properties._box_child)
        .forEach((line) => {
          const card = this.cards.find((item) => item.variants.some((variant) => variant.id === line.variant_id));
          if (card) card.selectVariantId(line.variant_id, line.quantity);
        });

      this.text.cta = this.text.update;
      this.render();
    } catch (error) {
      console.error(error);
    }
  }
}

class BundleCard {
  constructor(element, box) {
    this.el = element;
    this.box = box;

    this.productId = parseInt(element.dataset.productId, 10);
    this.title = element.dataset.productTitle;
    const variantData = element.querySelector('[data-bundle-variants]');
    this.variants = JSON.parse(variantData ? variantData.textContent : '[]');

    this.image = element.querySelector('[data-bundle-image]');
    this.videoButton = element.querySelector('[data-card-video-open]');
    this.pill = element.querySelector('[data-bundle-pill]');
    this.meta = element.querySelector('[data-bundle-meta]');
    this.priceEl = element.querySelector('[data-bundle-price]');
    this.priceCompareEl = element.querySelector('[data-bundle-price-compare]');
    this.countEl = element.querySelector('[data-bundle-count]');
    this.addButton = element.querySelector('[data-bundle-add]');
    this.addLabel = element.querySelector('[data-bundle-add-label]');
    this.stepper = element.querySelector('[data-bundle-stepper]');
    this.qtyEl = element.querySelector('[data-bundle-qty]');
    this.colorButtons = Array.from(element.querySelectorAll('[data-bundle-color]'));
    this.sizeButtons = Array.from(element.querySelectorAll('[data-bundle-size]'));

    // How many of the selected variant the shopper is about to add. The
    // quantity already in the box is shown as a badge on the image instead.
    this.pendingQty = 1;

    // colour name -> swatch background, for the pill on the image
    this.swatchStyles = {};
    this.colorButtons.forEach((button) => {
      this.swatchStyles[button.dataset.bundleColor] = {
        value: button.dataset.swatch,
        focal: button.dataset.swatchFocal,
      };
    });

    const firstAvailable = this.variants.find((variant) => variant.available) || this.variants[0] || {};
    this.color = this.colorButtons.length ? firstAvailable.color || '' : '';
    // Only auto-pick a size when there is nothing to choose.
    this.size = this.sizeButtons.length > 1 ? '' : firstAvailable.size || '';

    // Option changes affect this card only — re-rendering all 50 cards on every
    // swatch click is what made the swap feel sluggish.
    this.colorButtons.forEach((button) =>
      button.addEventListener('click', () => {
        this.color = button.dataset.bundleColor;
        if (this.size && !this.variantFor(this.color, this.size)) this.size = '';
        this.render();
      })
    );

    this.sizeButtons.forEach((button) =>
      button.addEventListener('click', () => {
        this.size = button.dataset.bundleSize;
        this.render();
      })
    );

    // Warm the other colours before they're asked for — but only for a card the
    // pointer actually settles on, so sweeping across the grid costs nothing.
    let preloadTimer = null;
    element.addEventListener(
      'pointerenter',
      () => {
        preloadTimer = setTimeout(() => this.preloadImages(), 140);
      },
      { passive: true }
    );
    element.addEventListener(
      'pointerleave',
      () => {
        clearTimeout(preloadTimer);
      },
      { passive: true }
    );
    ['touchstart', 'focusin'].forEach((event) =>
      element.addEventListener(event, () => this.preloadImages(), { once: true, passive: true })
    );

    this.addButton.addEventListener('click', () => {
      const variant = this.currentVariant();
      if (!variant || !variant.available) return;

      const adding = this.pendingQty;
      this.pendingQty = 1;

      this.box.setQuantity(variant.id, this.box.quantityFor(variant.id) + adding, {
        variantId: variant.id,
        productId: this.productId,
        title: this.title,
        color: variant.color,
        size: variant.size,
        price: variant.price,
        image: variant.image,
      });
    });

    element.querySelector('[data-bundle-plus]').addEventListener('click', () => {
      this.pendingQty = Math.min(this.pendingQty + 1, Math.max(this.box.room, 1));
      this.render();
    });

    element.querySelector('[data-bundle-minus]').addEventListener('click', () => {
      this.pendingQty = Math.max(this.pendingQty - 1, 1);
      this.render();
    });
  }

  /** Fetches this product's distinct variant images so a swatch click is instant. */
  preloadImages() {
    if (this.preloaded) return;
    this.preloaded = true;

    const seen = new Set();
    for (const variant of this.variants) {
      if (!variant.image || seen.has(variant.image)) continue;
      seen.add(variant.image);
      if (seen.size > 12) break;
      const image = new Image();
      image.decoding = 'async';
      image.src = variant.image;
    }
  }

  variantFor(color, size) {
    return this.variants.find(
      (variant) => (!this.colorButtons.length || variant.color === color) && (!this.sizeButtons.length || variant.size === size)
    );
  }

  currentVariant() {
    if (this.sizeButtons.length && !this.size) return null;
    if (this.colorButtons.length && !this.color) return null;
    return this.variantFor(this.color, this.size) || null;
  }

  /**
   * What the card should show. Falls back to the best variant of the selected
   * colour so the image and price follow a swatch click before a size is
   * chosen — the same behaviour as the collection cards.
   */
  previewVariant() {
    const exact = this.currentVariant();
    if (exact) return exact;
    if (!this.colorButtons.length || !this.color) return null;

    const sameColor = this.variants.filter((variant) => variant.color === this.color);
    return sameColor.find((variant) => variant.available) || sameColor[0] || null;
  }

  /**
   * Swapping `src` alone does nothing while a `srcset` is on the element, so
   * rebuild the candidate list around the new image.
   */
  setImage(url, alt) {
    if (!this.image || !url) return;
    if (this.image.dataset.currentSrc === url) return;

    this.image.dataset.currentSrc = url;
    this.image.src = url;

    if (/[?&]width=\d+/.test(url)) {
      this.image.srcset = [300, 600, 900]
        .map((width) => `${url.replace(/([?&])width=\d+/, `$1width=${width}`)} ${width}w`)
        .join(', ');
    } else {
      this.image.removeAttribute('srcset');
    }

    if (alt) this.image.alt = alt;
  }

  applyVideo(video) {
    if (!this.videoButton) return;
    if (!video || !video.src) {
      this.videoButton.hidden = true;
      this.videoButton.removeAttribute('data-video-src');
      return;
    }

    this.videoButton.hidden = false;
    this.videoButton.dataset.videoKind = video.kind || 'file';
    this.videoButton.dataset.videoSrc = video.src;
    if (video.poster) this.videoButton.dataset.videoPoster = video.poster;
    else delete this.videoButton.dataset.videoPoster;
  }

  /** Used by edit mode to restore a saved pick. */
  selectVariantId(variantId, quantity) {
    const variant = this.variants.find((item) => item.id === variantId);
    if (!variant) return;
    this.color = variant.color;
    this.size = variant.size;
    this.box.setQuantity(variantId, quantity, {
      variantId: variant.id,
      productId: this.productId,
      title: this.title,
      color: variant.color,
      size: variant.size,
      price: variant.price,
      image: variant.image,
    });
  }

  render() {
    const variant = this.currentVariant();

    this.colorButtons.forEach((button) => {
      const isSelected = button.dataset.bundleColor === this.color;
      const hasStock = this.variants.some(
        (item) => item.color === button.dataset.bundleColor && item.available
      );
      button.classList.toggle('is-selected', isSelected);
      button.classList.toggle('is-unavailable', !hasStock);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.sizeButtons.forEach((button) => {
      const size = button.dataset.bundleSize;
      const match = this.variantFor(this.color, size);
      const isSelected = size === this.size;
      button.classList.toggle('is-selected', isSelected);
      button.classList.toggle('is-unavailable', !match || !match.available);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (this.pill) {
      const swatch = this.color ? this.swatchStyles[this.color] : null;
      this.pill.hidden = !swatch || !swatch.value;
      if (swatch && swatch.value) {
        this.pill.style.setProperty('--bundle-swatch', swatch.value);
        if (swatch.focal) {
          this.pill.style.setProperty('--bundle-swatch-focal', swatch.focal);
        } else {
          this.pill.style.removeProperty('--bundle-swatch-focal');
        }
      }
    }

    const preview = this.previewVariant();

    if (preview) {
      this.setImage(preview.image, `${this.title} — ${[preview.color, preview.size].filter(Boolean).join(' / ')}`);
      this.applyVideo(preview.video);
      if (this.priceEl) this.priceEl.textContent = this.box.formatMoney(this.box.discountedPrice(preview.price));
      if (this.priceCompareEl) this.priceCompareEl.textContent = preview.priceFormatted;
      if (this.meta) {
        const details = [preview.color, this.size].filter(Boolean).join(' · ');
        if (details) this.meta.textContent = details;
      }
    }

    const productQuantity = this.box.quantityForProduct(this.productId);

    if (this.countEl) {
      this.countEl.hidden = productQuantity === 0;
      this.countEl.textContent = productQuantity;
    }
    this.el.classList.toggle('is-selected', productQuantity > 0);

    // Never offer to add more than the box can still hold.
    this.pendingQty = Math.min(Math.max(this.pendingQty, 1), Math.max(this.box.room, 1));
    if (this.qtyEl) this.qtyEl.textContent = this.pendingQty;

    // Stepper and add button sit side by side, so both stay visible and are
    // enabled or disabled on their own terms.
    const plus = this.el.querySelector('[data-bundle-plus]');
    const minus = this.el.querySelector('[data-bundle-minus]');
    if (plus) plus.disabled = !variant || !variant.available || this.pendingQty >= this.box.room;
    if (minus) minus.disabled = this.pendingQty <= 1;

    let label = this.box.text.add;
    let disabled = false;

    if (this.sizeButtons.length && !this.size) {
      label = this.box.text.chooseSize;
      disabled = true;
    } else if (!variant || !variant.available) {
      label = this.box.text.soldOut;
      disabled = true;
    } else if (this.box.isFull) {
      label = this.box.text.full;
      disabled = true;
    }

    this.addLabel.textContent = label;
    this.addButton.disabled = disabled;
  }
}

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (character) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
  });
}

/* Shopify money formatting, trimmed down from Shopify's own helper. */
function formatBundleMoney(cents, format) {
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const value = typeof cents === 'string' ? parseFloat(cents.replace('.', '')) : cents;

  function withDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (isNaN(number) || number == null) return '0';
    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  const match = (format || '').match(placeholder);
  if (!match) return withDelimiters(value);

  let formatted;
  switch (match[1]) {
    case 'amount':
      formatted = withDelimiters(value, 2);
      break;
    case 'amount_no_decimals':
      formatted = withDelimiters(value, 0);
      break;
    case 'amount_with_comma_separator':
      formatted = withDelimiters(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      formatted = withDelimiters(value, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      formatted = withDelimiters(value, 2, "'", '.');
      break;
    case 'amount_with_space_separator':
      formatted = withDelimiters(value, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      formatted = withDelimiters(value, 0, ' ', ',');
      break;
    default:
      formatted = withDelimiters(value, 2);
  }

  return format.replace(placeholder, formatted);
}

if (!customElements.get('bundle-box')) {
  customElements.define('bundle-box', BundleBox);
}
